
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import {
  FaUser,
  FaCube,
  FaCog,
  FaIdBadge,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaShieldAlt,
  FaTimes,
  FaUsers,
  FaLaptop,
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaHistory,
} from 'react-icons/fa';
import { Badge, Loading } from '../../../common';
import {
  formatType,
  getRiskClassName,
  getInitials,
} from '../../../../utils/helpers';
import { TYPE_COLORS } from '../../../../utils/constants';
import { useTheme } from "../../../../context/ThemeContext";
import './IdentityDetail.css';

/* ── Stable pastel palette ─────────────────────── */
const PASTEL_COLORS = [
  '#E3F2FD',
  '#E8F5E9',
  '#FFF3E0',
  '#F3E5F5',
  '#E0F7FA',
  '#FCE4EC',
];

/* ── Type helpers ──────────────────────────────── */
const getTypeIcon = (type) => {
  const icons = {
    human: <FaUser />,
    application: <FaCube />,
    'service-principal': <FaCog />,
    'managed-identity': <FaIdBadge />,
  };
  return icons[type] || <FaUser />;
};

const getTypeVariant = (type) => {
  const variants = {
    human: 'primary',
    application: 'success',
    'service-principal': 'warning',
    'managed-identity': 'info',
  };
  return variants[type] || 'default';
};

/* ── Main component ────────────────────────────── */
const IdentityDetail = ({ identity, isOpen, onClose, remediation, loading }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeView, setActiveView] = useState('remediation'); // 'remediation', 'groups', or 'devices'
  const [remediationTab, setRemediationTab] = useState('active');
  const modalRef = useRef(null);
  const { isDark } = useTheme();
  
  /* ── Focus trap & background interaction ────── */
  useEffect(() => {
    if (!isOpen) return;

    const appContent = document.getElementById("app-content");
    const previousOverflow = document.body.style.overflow;
    const previousFocusedElement = document.activeElement;

    /**
     * Disable background interaction and keyboard tab focus.
     * Important: modal must be rendered outside #app-content using portal.
     */
    appContent?.setAttribute("inert", "");
    appContent?.setAttribute("aria-hidden", "true");

    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (isOpen && e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      appContent?.removeAttribute("inert");
      appContent?.removeAttribute("aria-hidden");

      document.body.style.overflow = previousOverflow;
      document.body.style.overflow = "unset";

      document.removeEventListener("keydown", handleEscape);

      if (previousFocusedElement?.focus) {
        previousFocusedElement.focus();
      }
    };
  }, [isOpen, onClose]);


  // Reset view when modal closes
  useEffect(() => {
    if (!isOpen) setActiveView('remediation');
  }, [isOpen]);

  if (!identity) return null;

  // const modifierMap = {
  //   remediation: 'default',
  //   groups: 'groups',
  //   devices: 'devices',
  //   appRoles: 'app-roles',
  //   roles: 'roles'
  // };

  // const currentModifier = modifierMap[activeView] || 'default';

  const toggleAccordion = (index) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  const getPastelColor = (index) =>
    PASTEL_COLORS[index % PASTEL_COLORS.length];

  const allUseCases =
    remediation?.attack_vectors?.flatMap(v => v.use_cases || []) || [];

  const activeCount = allUseCases.filter(uc => !uc.backlogged).length;
  const backlogCount = allUseCases.filter(uc => uc.backlogged).length;


   return createPortal(
    <div className={`id-modal-backdrop ${isDark ? 'dark dark-theme' : 'light light-theme'}`}>
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          initialFocus: () =>
            modalRef.current?.querySelector(".id-modal-close") || modalRef.current,
          fallbackFocus: () => modalRef.current,
          returnFocusOnDeactivate: true,
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: true,
        }}
      >
        <div
          ref={modalRef}
          className={`id-modal-container ${isOpen ? 'id-modal-container--open' : ''}`}
          role="dialog"
          aria-labelledby="id-modal-title"
          aria-modal="true"
          tabIndex={-1}
          onClick={(e) => {
            // ✅ Only close if click is NOT inside the modal content
            if (!e.target.closest('.id-modal-content')) {
              onClose();
            }
          }}
        >
          <div className="id-modal-content">
            {/* ── Modal header ──────────────────── */}
            <div className="id-modal-header">
              <h2 id="id-modal-title" className="id-modal-title">
                {activeView === 'remediation' ? 'Identity Risk Remediations' : `Identity ${activeView.charAt(0).toUpperCase() + activeView.slice(1)}`}
              </h2>
              <button className="id-modal-close" onClick={onClose} aria-label="Close modal" type="button">
                <FaTimes size={18} />
              </button>
            </div>

            <div className="id-modal-body">
              <div className="identity-detail">

                {/* ── Header with Integrated Buttons ── */}
                <div className="detail-header" >
                  <div className="detail-sub-header">
                    <div
                      className="detail-avatar"
                      style={{
                        backgroundColor: `${TYPE_COLORS[identity?.type]}15`,
                        color: TYPE_COLORS[identity?.type],
                      }}
                    >
                      {getInitials(identity?.name)}
                    </div>

                    <div className="detail-header-info">
                      <h3 className="detail-name">{identity?.name}</h3>
                      <div className="detail-badges">
                        <Badge variant={getTypeVariant(identity?.type)}>
                          {getTypeIcon(identity?.type)}
                          <span>{formatType(identity?.type)}</span>
                        </Badge>
                        <Badge variant={getRiskClassName(identity?.riskLevel)}>
                          <FaExclamationTriangle />
                          <span>{identity?.risk?.stage} Risk</span>
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="idm-view-selector__container">
                    <div className="idm-view-selector__wrapper">
                      <select
                        value={activeView}
                        onChange={(e) => setActiveView(e.target.value)}
                        className={`idm-view-selector__select idm-view-selector__select--default`}
                      >
                        <option value="remediation">Remediation</option>

                        <option value="groups">
                          Groups ({identity?.groups?.length || 0})
                        </option>

                        <option value="devices">
                          Devices ({identity?.device?.length || 0})
                        </option>

                        <option value="appRoles">
                          App Role Assignment ({identity?.appRoleAssignments?.length || 0})
                        </option>

                        <option value="roles">
                          Roles ({identity?.roles?.length || 0})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ── Conditional Content Area ── */}
                <div className="detail-section">

                  {/* 1. ATTACK VECTORS VIEW */}

                  {activeView === 'remediation' && (
                    <>
                      <div className="fr-header-container">
                        <h4 className="section-title">
                          <FaShieldAlt className="section-title-icon" />
                          Findings &amp; Remediations
                        </h4>

                        {/* Sub-tabs for Backlog filtering */}
                        <div className="fr-subtabs-wrapper">
                          <button
                            onClick={() => setRemediationTab('active')}
                            className={`fr-filter-btn ${remediationTab === 'active' ? 'fr-filter-btn--active' : ''}`}
                          >
                            Active ({activeCount})
                          </button>

                          <button
                            onClick={() => setRemediationTab('backlogged')}
                            className={`fr-filter-btn ${remediationTab === 'backlogged' ? 'fr-filter-btn--active' : ''}`}
                          >
                            Backlog ({backlogCount})
                          </button>
                        </div>
                      </div>

                      {loading ? (
                        <div className="id-loading">
                          <span className="id-loading-spinner" aria-hidden="true" />
                          <span>Loading remediation…</span>
                        </div>
                      ) : remediation ? (
                        <div className="accordion-container" role="list">
                          {remediation?.attack_vectors?.map((vector, idx) => {
                            // FILTER: Only get use cases that match the current tab
                            const filteredUseCases = vector?.use_cases?.filter(uc =>
                              remediationTab === 'backlogged' ? uc.backlogged === true : !uc.backlogged
                            ) || [];

                            // If no use cases match for THIS specific vector, don't show the vector at all
                            if (filteredUseCases.length === 0) return null;

                            const isExpanded = openIndex === idx;
                            const bgColor = getPastelColor(idx);

                            return (
                              <div key={idx} className={`accordion-item ${isExpanded ? 'accordion-item--open' : ''}`} style={{ backgroundColor: bgColor }} role="listitem">
                                <button className="accordion-header" onClick={() => toggleAccordion(idx)} type="button">
                                  <div className="accordion-left">
                                    <span className="vector-name">{vector?.attack_vector_name?.replace(/_/g, ' ')}</span>
                                    <span className="usecase-count">
                                      {filteredUseCases.length} {filteredUseCases.length === 1 ? 'remediation' : 'remediations'}
                                    </span>
                                  </div>
                                  <span className="accordion-chevron">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                                </button>
                                {isExpanded && (
                                  <div className="accordion-content">
                                    {filteredUseCases.map((uc, i) => (
                                      <div key={i} className="use-case-card">
                                        <div className="use-case-reason">
                                          <span className="use-case-label">Reason</span>
                                          <p className="use-case-text">{uc?.reason}</p>
                                        </div>
                                        <div className="use-case-remediation">
                                          <span className="use-case-label">Fix</span>
                                          <p className="use-case-text">{uc?.remediation}</p>
                                        </div>
                                        {uc.backlogged && (
                                          <div style={{ marginTop: '8px' }}>
                                            <Badge variant="warning" style={{ fontSize: '10px' }}>Backlogged</Badge>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Helper message if the overall list is empty after filtering */}
                          {remediation?.attack_vectors?.every(v =>
                            (v.use_cases?.filter(uc => remediationTab === 'backlogged' ? uc.backlogged === true : !uc.backlogged).length === 0)
                          ) && (
                              <p className="no-data" style={{ textAlign: 'center', padding: '20px' }}>
                                No {remediationTab} remediations found.
                              </p>
                            )}
                        </div>
                      ) : (
                        <p className="no-data">No remediation data available.</p>
                      )}
                    </>
                  )}


                  {/* 2. GROUPS VIEW */}
                  {activeView === 'groups' && (
                    <>
                      <h4 className="section-title ag-section-title-container">
                        <span><FaUsers className="section-title-icon" /> Associated Groups</span>
                      </h4>

                      {identity?.groups?.length > 0 ? (
                        <div className="ag-list-wrapper">
                          {identity.groups.map((group, idx) => (
                            <div key={group?.group_id || idx} className="ag-card">
                              <div className="ag-card-name">{group?.displayName}</div>
                              {group?.description && (
                                <div className="ag-card-desc">{group.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No groups found for this identity.</p>
                      )}
                    </>
                  )}

                  {/* 3. DEVICES VIEW */}
                  {activeView === 'devices' && (
                    <>
                      <h4 className="section-title rd-section-title-container">
                        <span><FaLaptop className="section-title-icon" /> Registered Devices</span>
                      </h4>

                      {identity?.device?.length > 0 ? (
                        <div className="rd-list-wrapper">
                          {identity.device.map((dev, idx) => (
                            <div key={dev?.id || idx} className="rd-card">

                              <div className="rd-card-header">
                                <div className="rd-card-name">{dev?.displayName}</div>

                                {/* Compliance Status Badge */}
                                {dev?.isCompliant === true ? (
                                  <div className="rd-badge rd-badge--compliant">
                                    <FaCheckCircle />
                                    <span>Compliant</span>
                                  </div>
                                ) : dev?.isCompliant === false ? (
                                  <div className="rd-badge rd-badge--non-compliant">
                                    <FaTimesCircle />
                                    <span>Non-compliant</span>
                                  </div>
                                ) : (
                                  <div className="rd-badge rd-badge--unknown">
                                    <FaQuestionCircle />
                                    <span>Unknown</span>
                                  </div>
                                )}
                              </div>

                              <div className="rd-card-footer">
                                <span>OS: {dev?.operatingSystem || 'Unknown'}</span>
                                <span className="rd-card-id">ID: {dev?.id?.substring(0, 13)}...</span>
                              </div>

                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No devices found for this identity.</p>
                      )}
                    </>
                  )}


                  {/* 3. DEVICES VIEW */}
                  {activeView === 'appRoles' && (
                    <>
                      <h4 className="section-title ar-section-title-container">
                        <span><FaLaptop className="section-title-icon" /> App Role Assignments</span>
                      </h4>

                      {identity?.appRoleAssignments?.length > 0 ? (
                        <div className="ar-list-wrapper">
                          {identity.appRoleAssignments.map((dev, idx) => (
                            <div key={dev?.id || idx} className="ar-card">
                              <div className="ar-card-header">
                                <div className="ar-card-name">{dev?.resourceDisplayName}</div>

                                <div className="ar-principal-badge">
                                  <div className="ar-principal-id">{dev?.principalId}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No App Role Assignments found for this identity.</p>
                      )}
                    </>
                  )}

                  {/* 4. Roles */}

                  {activeView === 'roles' && (
                    <>
                      <h4 className="section-title ir-section-title-container">
                        <span><FaLaptop className="section-title-icon" /> App Role Assignments</span>
                      </h4>

                      {identity?.roles?.length > 0 ? (
                        <div className="ir-list-wrapper">
                          {identity.roles.map((dev, idx) => (
                            <div key={dev?.id || idx} className="ir-card">
                              <div className="ir-card-header">

                                <div>
                                  {/* Role Name */}
                                  <div className="ir-role-name">{dev?.name}</div>
                                </div>

                                {/* Badges container */}
                                <div className="ir-badges-container">
                                  {/* Admin Badge */}
                                  {dev?.is_admin && (
                                    <span className="ir-badge-admin">Admin</span>
                                  )}
                                </div>

                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No Roles found for this identity.</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FocusTrap>
    </div>,
    document.body
  );
};

export default IdentityDetail;